import { useEffect, useMemo, useState } from "react";
import { Users, Diamond, TrendingUp, Activity, Download } from "lucide-react";
import api from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import StatCard from "@/components/StatCard";

const PLATFORMS = [
  { key: "all", label: "All Platforms" },
  { key: "trumeet", label: "Trumeet" },
  { key: "olaparty", label: "Qwick Live" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [daily, setDaily] = useState(null);
  const [bySeller, setBySeller] = useState([]);
  const [platform, setPlatform] = useState("all");
  const [days, setDays] = useState(30);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const rangeActive = Boolean(startDate && endDate);

  useEffect(() => {
    (async () => {
      const s = await api.get("/admin/stats");
      setStats(s.data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const daily = new URLSearchParams();
      const br = new URLSearchParams();
      if (platform !== "all") { daily.set("platform", platform); br.set("platform", platform); }
      if (rangeActive) {
        daily.set("start_date", startDate); daily.set("end_date", endDate);
        br.set("start_date", startDate); br.set("end_date", endDate);
      } else {
        daily.set("days", String(days));
      }
      const [d, b] = await Promise.all([
        api.get(`/admin/sales/daily?${daily.toString()}`),
        api.get(`/admin/sales/by-reseller${br.toString() ? `?${br.toString()}` : ""}`),
      ]);
      setDaily(d.data);
      setBySeller(b.data);
    })();
  }, [platform, days, startDate, endDate, rangeActive]);

  const maxDaily = useMemo(
    () => Math.max(1, ...(daily?.rows || []).map((r) => r.diamonds)),
    [daily]
  );

  const exportBySeller = () => {
    const suffix = rangeActive ? `${startDate}_to_${endDate}` : `last-${days}d`;
    const headers = ["Reseller", "Email", "Phone", "City", "State", "Status", "Recharges", "Diamonds sold", rangeActive ? "Allocated (range)" : "Allocated", "Balance"];
    const rows = bySeller.map((s) => [
      s.name || "", s.email || "", s.phone || "", s.city || "", s.state || "", s.status || "",
      s.recharges ?? 0, s.diamonds_sold ?? 0, s.allocated_in_range ?? 0, s.balance ?? 0,
    ]);
    downloadCsv(`sales-by-reseller_${platform}_${suffix}.csv`, headers, rows);
  };

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Overview</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900 mt-1">
          Control Console
        </h1>
        <p className="text-zinc-500 mt-2 max-w-2xl">
          Real-time view of resellers, diamonds in circulation, and recharge throughput.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Resellers"
          value={stats?.total_resellers ?? "—"}
          sub={`${stats?.active_resellers ?? 0} active`}
          icon={Users}
          testId="stat-resellers"
        />
        <StatCard
          label="Diamonds Allocated"
          value={(stats?.diamonds_allocated ?? 0).toLocaleString()}
          sub={`${(stats?.diamonds_outstanding ?? 0).toLocaleString()} outstanding`}
          icon={Diamond}
          accent="bg-zinc-900 text-white"
          testId="stat-allocated"
        />
        <StatCard
          label="Total Recharges"
          value={(stats?.total_recharges ?? 0).toLocaleString()}
          sub={`${(stats?.success_recharges ?? 0).toLocaleString()} successful`}
          icon={Activity}
          testId="stat-recharges"
        />
        <StatCard
          label="Success Rate"
          value={`${(stats?.success_rate ?? 0).toFixed(1)}%`}
          sub="last 500 records"
          icon={TrendingUp}
          testId="stat-success-rate"
        />
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3" data-testid="sales-filters">
        <div className="flex flex-wrap gap-2" data-testid="platform-filter">
          {PLATFORMS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPlatform(p.key)}
              data-testid={`filter-platform-${p.key}`}
              className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                platform === p.key
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <span className="text-xs text-zinc-500">Quick:</span>
          {[7, 30, 90].map((n) => (
            <button
              key={n}
              onClick={() => { setStartDate(""); setEndDate(""); setDays(n); }}
              data-testid={`filter-days-${n}`}
              className={`px-2.5 py-1 rounded text-xs font-medium ${
                !rangeActive && days === n ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
              }`}
            >
              {n}d
            </button>
          ))}
          <span className="mx-1 h-4 w-px bg-zinc-200" />
          <span className="text-xs text-zinc-500">From</span>
          <input
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={(e) => setStartDate(e.target.value)}
            data-testid="filter-start-date"
            className="h-8 px-2 text-xs rounded-md border border-zinc-300 bg-white"
          />
          <span className="text-xs text-zinc-500">To</span>
          <input
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => setEndDate(e.target.value)}
            data-testid="filter-end-date"
            className="h-8 px-2 text-xs rounded-md border border-zinc-300 bg-white"
          />
          {rangeActive && (
            <button
              onClick={() => { setStartDate(""); setEndDate(""); }}
              data-testid="filter-clear-dates"
              className="px-2.5 py-1 rounded text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Daily sales */}
      <div className="bg-white border border-zinc-200 rounded-lg" data-testid="daily-sales-card">
        <div className="flex items-end justify-between p-6 border-b border-zinc-100 flex-wrap gap-3">
          <div>
            <h2 className="font-display text-lg font-medium text-zinc-900">Daily sales</h2>
            <p className="text-sm text-zinc-500 mt-0.5">
              {rangeActive
                ? `Successful recharges · ${daily?.start_date} → ${daily?.end_date}`
                : `Successful recharges across ${daily?.days ?? days} days`} · {daily?.platform === "all" ? "all platforms" : daily?.platform}
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Total in range</div>
            <div className="font-display text-2xl font-semibold tabular-nums font-mono text-zinc-900" data-testid="daily-total-diamonds">
              {(daily?.total_diamonds ?? 0).toLocaleString()}
            </div>
            <div className="text-xs text-zinc-500 font-mono">
              {(daily?.total_recharges ?? 0).toLocaleString()} recharges
            </div>
          </div>
        </div>
        {/* Mini bar chart */}
        <div className="px-6 pt-6">
          <div className="flex items-end gap-1 h-28">
            {(daily?.rows || []).map((r) => {
              const h = Math.round((r.diamonds / maxDaily) * 100);
              return (
                <div
                  key={r.date}
                  title={`${r.date}: ${r.diamonds.toLocaleString()} diamonds · ${r.recharges} txns`}
                  className="flex-1 bg-zinc-200 hover:bg-zinc-900 transition-colors rounded-t-sm"
                  style={{ height: `${Math.max(2, h)}%` }}
                  data-testid={`daily-bar-${r.date}`}
                />
              );
            })}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <Th>Date</Th>
                <Th className="text-right">Recharges</Th>
                <Th className="text-right">Diamonds</Th>
              </tr>
            </thead>
            <tbody>
              {(daily?.rows || []).slice().reverse().map((r) => (
                <tr key={r.date} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`daily-row-${r.date}`}>
                  <Td mono>{r.date}</Td>
                  <Td className="text-right tabular-nums font-mono">{r.recharges.toLocaleString()}</Td>
                  <Td className="text-right tabular-nums font-mono font-semibold text-zinc-900">{r.diamonds.toLocaleString()}</Td>
                </tr>
              ))}
              {!daily?.rows?.length && (
                <tr><td colSpan="3" className="px-6 py-12 text-center text-sm text-zinc-400">No sales recorded</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sales by reseller */}
      <div className="bg-white border border-zinc-200 rounded-lg" data-testid="reseller-sales-card">
        <div className="flex items-start justify-between p-6 border-b border-zinc-100 flex-wrap gap-3">
          <div>
            <h2 className="font-display text-lg font-medium text-zinc-900">Sales by reseller</h2>
            <p className="text-sm text-zinc-500 mt-0.5">
              {rangeActive
                ? `Sales & allocations · ${startDate} → ${endDate}, ranked by diamonds sold.`
                : "Totals across all time, ranked by diamonds sold."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="reseller-sales-date-filter">
            <button
              onClick={exportBySeller}
              disabled={!bySeller.length}
              data-testid="export-by-reseller-csv"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-40"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
            <span className="mx-1 h-4 w-px bg-zinc-200" />
            <span className="text-xs text-zinc-500">From</span>
            <input
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="reseller-sales-start-date"
              className="h-8 px-2 text-xs rounded-md border border-zinc-300 bg-white"
            />
            <span className="text-xs text-zinc-500">To</span>
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="reseller-sales-end-date"
              className="h-8 px-2 text-xs rounded-md border border-zinc-300 bg-white"
            />
            {rangeActive && (
              <button
                onClick={() => { setStartDate(""); setEndDate(""); }}
                data-testid="reseller-sales-clear-dates"
                className="px-2.5 py-1 rounded text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <Th>Reseller</Th>
                <Th>Phone</Th>
                <Th>Location</Th>
                <Th className="text-right">Recharges</Th>
                <Th className="text-right">Diamonds sold</Th>
                <Th className="text-right">{rangeActive ? "Allocated (range)" : "Allocated"}</Th>
                <Th className="text-right">Balance</Th>
              </tr>
            </thead>
            <tbody>
              {bySeller.length === 0 && (
                <tr><td colSpan="7" className="px-6 py-12 text-center text-sm text-zinc-400">No resellers yet</td></tr>
              )}
              {bySeller.map((s) => (
                <tr key={s.reseller_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`reseller-sales-row-${s.reseller_id}`}>
                  <Td>
                    <div className="font-medium text-zinc-900">{s.name || "—"}</div>
                    <div className="text-xs text-zinc-500">{s.email}</div>
                  </Td>
                  <Td mono>{s.phone || "—"}</Td>
                  <Td>{[s.city, s.state].filter(Boolean).join(", ") || "—"}</Td>
                  <Td className="text-right tabular-nums font-mono">{s.recharges.toLocaleString()}</Td>
                  <Td className="text-right tabular-nums font-mono font-semibold text-zinc-900">{s.diamonds_sold.toLocaleString()}</Td>
                  <Td className="text-right tabular-nums font-mono">{(s.allocated_in_range ?? 0).toLocaleString()}</Td>
                  <Td className="text-right tabular-nums font-mono">{s.balance.toLocaleString()}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3 ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = "", mono }) => (
  <td className={`px-6 py-3 text-sm text-zinc-700 ${mono ? "font-mono" : ""} ${className}`}>{children}</td>
);
